import type { ReactElement, ReactNode } from 'react'
import { STATUS_TONE_CLASSES } from './status-tone.ts'
import type { StatusTone } from './status-tone.ts'

/**
 * A banner that explains a condition: a failed save, a staged migration, a
 * warning that verification reads the saved values and not your edits.
 *
 * The tone names here are the reader's vocabulary (`info`, `success`), not
 * the token layer's (`pending`, `done`), and the mapping between them is
 * deliberate rather than mechanical:
 *
 * - `info` -> `pending`, the one cool/blue family. Informational and
 *   "queued" are the same temperature and never appear as each other.
 * - `success` -> `done`, which is near-grey on purpose. A green banner for
 *   "saved" is exactly the noise that trains an operator past the red one.
 *
 * Non-colour signalling: each tone gets a short word in the heading slot
 * when no `title` is given, so the family is legible without the hue.
 */

export type CalloutTone = 'info' | 'success' | 'warn' | 'danger'

const TONE_STATUS: Record<CalloutTone, StatusTone> = {
  info: 'pending',
  success: 'done',
  warn: 'warn',
  danger: 'danger',
}

/** The fallback heading. Carries the meaning when the colour cannot —
 * for a deuteranope, for a monochrome display, for a screen reader. */
const TONE_FALLBACK_TITLE: Record<CalloutTone, string> = {
  info: 'Note',
  success: 'Done',
  warn: 'Warning',
  danger: 'Error',
}

export interface CalloutProps {
  tone: CalloutTone
  /** Overrides the tone's default heading. */
  title?: string
  children: ReactNode
  /** A single control — "Retry", "Open the approval". Right-aligned. */
  action?: ReactNode
}

export function Callout({
  tone,
  title,
  children,
  action,
}: CalloutProps): ReactElement {
  return (
    <div
      // `alert` only for the two tones a human has to act on. An
      // informational banner that interrupts a screen reader mid-sentence
      // is worse than one read in document order.
      role={tone === 'danger' || tone === 'warn' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-md border px-3 py-2 text-sm ${STATUS_TONE_CLASSES[TONE_STATUS[tone]]}`}
    >
      <div className="min-w-0 flex-1">
        <p className="font-medium">{title ?? TONE_FALLBACK_TITLE[tone]}</p>
        <div className="mt-0.5">{children}</div>
      </div>
      {action !== undefined && <div className="shrink-0">{action}</div>}
    </div>
  )
}
